import pluralize from "pluralize"
import PropTypes from "prop-types"
import React from "react"
import { useMarina } from "src/contexts/MarinaContext"
import { invoicePreviewTaxRows } from "src/main/Service/Manage/shared/invoicePreviewTaxRows"
import { pricePrecisionDigits } from "src/main/Service/Manage/shared/pricePrecision"

import Badge from "src/components/Badge"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"

import JobStatusBadge from "../JobStatusBadge"
import MarinaLetterhead from "./MarinaLetterhead"
import PrintContactValue from "./PrintContactValue"
import PrintIdentGrid from "./PrintIdentGrid"
import PrintRequestDescription from "./PrintRequestDescription"

const WO_STATUS_BADGE_COLOR = {
  open: "gray",
  in_progress: "blue",
  work_complete: "supplementary-violet",
  closed: "teal",
}

const CustomerCopyDocument = ({ printView }) => {
  const { presentmentCurrency } = useMarina()
  const { marina, workOrder, contactBoat, jobs, totals } = printView
  const money = (amount) =>
    formatCurrencyFromMinorUnits({ amount, currency: presentmentCurrency })
  const boatCell = [contactBoat?.name, workOrder.assignedSpaceName]
    .filter(Boolean)
    .join(" · ")
  const isEstimate =
    jobs.length === 0 || jobs.some((job) => job.status !== "invoiced")

  return (
    <div className="p-2 text-gray-900">
      <MarinaLetterhead
        marina={marina}
        right={
          <>
            <div className="text-2xl font-bold tracking-wide">WORK ORDER</div>
            <div className="mt-2">
              <Badge
                variant="small"
                color={WO_STATUS_BADGE_COLOR[workOrder.status] || "gray"}
                text={workOrder.statusLabel}
              />
            </div>
          </>
        }
      />
      <PrintIdentGrid
        cells={[
          { label: "Work order #", value: workOrder.displaySequentialId },
          { label: "Customer", value: workOrder.contactName },
          { label: "Boat · Space", value: boatCell },
          { label: "Created", value: workOrder.createdAtFormatted },
          { label: "Scheduled", value: workOrder.scheduledAtFormatted },
          {
            label: "Contact",
            value:
              workOrder.contactPhone || workOrder.contactEmail ? (
                <PrintContactValue
                  phone={workOrder.contactPhone}
                  email={workOrder.contactEmail}
                />
              ) : null,
          },
        ]}
      />
      <PrintRequestDescription description={workOrder.requestDescription} />

      <div className="pt-5">
        {jobs.map((job) => (
          <div
            key={job.id}
            className="mb-4 break-inside-avoid overflow-hidden rounded-lg border border-gray-300"
          >
            <div className="border-l-4 border-blue-500 bg-gray-100 px-4 py-2.5">
              <div className="flex items-center gap-2.5">
                <span className="text-sm font-bold">{job.name}</span>
                <JobStatusBadge status={job.status} />
              </div>
              {job.description && (
                <div className="ml-2.5 mt-1.5 border-l-2 border-gray-300 pl-2.5 text-xs italic text-gray-600">
                  {job.description}
                </div>
              )}
            </div>
            <div className="px-4">
              {job.lineItems.map((lineItem) => (
                <div
                  key={lineItem.id}
                  className="border-b border-gray-100 py-2.5"
                >
                  <div className="flex items-baseline gap-4">
                    <div className="flex-1 text-sm">{lineItem.name}</div>
                    <div className="font-mono whitespace-nowrap text-xs text-gray-500">
                      {parseFloat(lineItem.quantity)} ×{" "}
                      {formatCurrencyFromMinorUnits({
                        amount: lineItem.pricePerUnit,
                        currency: presentmentCurrency,
                        precision: pricePrecisionDigits(
                          lineItem.pricePrecision
                        ),
                      })}
                    </div>
                    <div className="font-mono min-w-[92px] text-right text-sm font-bold">
                      {money(lineItem.subtotal)}
                    </div>
                  </div>
                  {lineItem.notes && (
                    <div className="mt-1 max-w-[78%] border-l-2 border-gray-300 pl-3 text-xs text-gray-600">
                      {lineItem.notes}
                    </div>
                  )}
                </div>
              ))}
              <div className="flex items-baseline justify-between py-2.5">
                <div className="text-xxs font-bold uppercase tracking-wide text-gray-500">
                  Job subtotal
                </div>
                <div className="font-mono min-w-[92px] text-right text-sm font-bold">
                  {money(job.subtotal)}
                </div>
              </div>
            </div>
          </div>
        ))}

        <div className="flex break-inside-avoid justify-end pt-1.5">
          <div className="min-w-[280px]">
            <div className="flex items-baseline justify-between py-1.5 text-sm">
              <div className="text-gray-600">
                Subtotal ({jobs.length} {pluralize("job", jobs.length)})
              </div>
              <div className="font-mono text-gray-700">
                {money(totals.subtotal)}
              </div>
            </div>
            {invoicePreviewTaxRows(totals).map((tax) => (
              <div
                key={tax.key}
                className="flex items-baseline justify-between border-b border-gray-200 py-1.5 text-sm"
              >
                <div className="text-gray-600">{tax.label}</div>
                <div className="font-mono text-gray-700">
                  {money(tax.amount)}
                </div>
              </div>
            ))}
            <div className="flex items-baseline justify-between pt-2.5">
              <div className="text-base font-bold">
                {isEstimate ? "Estimated total" : "Total"}
              </div>
              <div className="font-mono text-lg font-bold">
                {money(totals.total)}
              </div>
            </div>
            {isEstimate && (
              <div className="mt-1.5 text-right text-xs italic text-gray-500">
                Estimate only — subject to change. Final charges may vary based
                on parts and labor.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

CustomerCopyDocument.propTypes = {
  printView: PropTypes.shape({
    marina: PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
      displayPhone: PropTypes.string,
      logoUrl: PropTypes.string,
      address: PropTypes.object,
    }).isRequired,
    workOrder: PropTypes.shape({
      displaySequentialId: PropTypes.string,
      status: PropTypes.string,
      statusLabel: PropTypes.string,
      createdAtFormatted: PropTypes.string,
      scheduledAtFormatted: PropTypes.string,
      requestDescription: PropTypes.string,
      contactName: PropTypes.string,
      contactPhone: PropTypes.string,
      contactEmail: PropTypes.string,
      assignedSpaceName: PropTypes.string,
    }).isRequired,
    contactBoat: PropTypes.shape({ name: PropTypes.string }),
    jobs: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
        name: PropTypes.string,
        description: PropTypes.string,
        status: PropTypes.string,
        subtotal: PropTypes.number,
        taxAmount: PropTypes.number,
        total: PropTypes.number,
        lineItems: PropTypes.array,
      })
    ).isRequired,
    totals: PropTypes.shape({
      subtotal: PropTypes.number,
      taxAmount: PropTypes.number,
      total: PropTypes.number,
      itemizedTaxes: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          rateDisplay: PropTypes.string,
          amount: PropTypes.number,
        })
      ),
    }).isRequired,
  }).isRequired,
}

export default CustomerCopyDocument
